import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Building2 } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useOrganizationScope } from "../contexts/OrganizationScopeContext";
import { Button } from "./ui/button";

export const OrganizationScopeGate = ({ children }) => {
  const {
    activeOrganizationId,
    canUseOrganizationScope,
    isPlatformAdmin,
    loading,
    organizationsError,
    refreshOrganizations,
    scopeVersion,
  } = useOrganizationScope();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await refreshOrganizations();
    } catch (error) {
      // organizationsError stays true; the banner below remains visible.
    } finally {
      setRetrying(false);
    }
  };

  if (isPlatformAdmin && loading) {
    return (
      <div className="flex min-h-[24rem] items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: "var(--brand-accent)", borderTopColor: "transparent" }} />
          <p style={{ color: "var(--text-secondary)" }}>{t.common.loading}</p>
        </div>
      </div>
    );
  }

  if (!canUseOrganizationScope) {
    return (
      <div className="flex min-h-[24rem] flex-col items-center justify-center text-center" data-testid="organization-scope-gate">
        <Building2 className="h-16 w-16 mb-4" style={{ color: "var(--text-muted)" }} />
        <h1 className="text-2xl font-semibold mb-2" style={{ color: "var(--text-primary)" }}>
          {t.organizationScope.selectTitle}
        </h1>
        <p className="max-w-lg mb-6" style={{ color: "var(--text-secondary)" }}>
          {t.organizationScope.selectDescription}
        </p>
        {organizationsError && (
          <div
            className="flex items-center gap-2 mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
            data-testid="organization-scope-load-error"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {t.organizationScope.loadError}
          </div>
        )}
        <div className="flex items-center gap-3">
          {organizationsError && (
            <Button
              variant="outline"
              onClick={handleRetry}
              disabled={retrying}
              data-testid="organization-scope-retry-button"
            >
              {t.organizationScope.retryAction}
            </Button>
          )}
          <Button onClick={() => navigate("/organizations")} data-testid="organization-scope-select-button">
            {t.organizationScope.selectAction}
          </Button>
        </div>
      </div>
    );
  }

  return React.cloneElement(children, {
    key: `${activeOrganizationId || "organization"}-${scopeVersion}`,
  });
};
